/**
 * DocumentTimeline - ASIC Document Timeline Component
 *
 * react-bootstrap ^1.6.1 + custom SCSS
 *
 * Props:
 *   data              Array    - Required. Array of document objects from the SP.
 *   loading           Boolean  - Optional. Shows loading state when true.
 *   error             String   - Optional. Displays error message if provided.
 *   onDocumentClick   Function - Optional. Callback when a document card is clicked.
 *
 * Usage:
 *   import DocumentTimeline from './DocumentTimeline';
 *   <DocumentTimeline data={documents} loading={isLoading} error={errorMsg} />
 */
import React, { useState, useEffect, useRef } from "react";
import { Modal, Spinner, Alert, Badge } from "react-bootstrap";
import "./DocumentTimeline.scss";

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════ */
const FORM_TITLES = {
  "A100":"Correction of state data - roles and addresses","A101":"Correction of state data - company detail","A103":"Report of DOCIMAGE error","A104":"Supplementary pages to imaged documents","A201":"Correction of registration details",
  "104":"Record of lodgement of documents","105":"Cover page for office copy of a court order","106":"Notification of notice to withdraw lodged document","107":"Notification of cancellation of company","110":"Record of lodgement of documents - Local ASIC Representative","128":"Commission's power to direct a registered name to be changed",
  "201":"Application for registration as an Australian company","202":"Application for registration of a body corporate as a company","203":"Notification of change of office hours or address","205":"Notification of resolution","205A":"Notification of Resolution - Change of Company Name","206":"Application for change of company type","207":"Notification of share issue","208":"Notification of details of shares allotted other than for cash","210":"Notification of statement of special rights carried by shares","211":"Notification of division or conversion of classes of shares","213":"Application for transfer of incorporation","214":"Application for certificate authorising an application for transfer of incorporation","215":"Notification Of Initial Appointment Of Officeholders",
  "2205":"Notification of resolutions regarding shares","2501":"Application for extension of time to hold AGM","2560":"Notification of reduction in share capital details","2601":"Notification of intention to give financial assistance","2602":"Notification of financial assistance details",
  "280":"Notification of share buy-back details","281":"Notice of intention to carry out a share buy-back","284":"Notification of share cancellation",
  "304":"Notification of change to officeholders","309":"Notification of details of a charge","310":"Notification of further issue of debentures in a series","311":"Notification of assignment or change to details of a charge","312":"Notification of discharge or release of property from a charge","313":"Notification of address in Australia of information relating to financial records kept outside Australia","314":"Notification of return of members of firm of auditors","315":"Notification of resignation, removal or cessation of auditor","316":"Annual Return of a Company","316L":"Company Annual Return",
  "342":"Application for consent to resign as auditor","350":"Certification of compliance with stamp duties law","361":"Notification of address for the purpose of service of annual return","362":"Notification of appointment or cessation as registered agent","370":"Notification by officeholder of resignation or retirement","371":"Copy of financial accounts and reports","377":"Notification as to the holding of office in a company","378":"Application to use an alternative address","379":"Notification of residential address or change in residential address","381":"Application for extension of time to lodge Annual Return","382":"Notification of resolutions for audit relief","384":"Notification of resolution by directors of a small proprietary company","385":"Notification by large proprietary company exempt from lodging financial statements","388":"Copy of financial statements and reports","389":"Annual notice by wholly-owned entity relieved from financial obligations",
  "401":"Application for registration as a registrable Australian body","402":"Application for registration as a foreign company","404":"Notification of change to agent of a foreign company","406":"Annual Return of a foreign company","407":"Notification of cessation, winding up or dissolution of a foreign company","409":"Notification of change to details of a foreign company","410":"Application for reservation of a name",
  "432":"Application to approve registration of a company name without the word 'limited'","433":"Notification of non-compliance with section 150 requirements",
  "480":"Annual Company Statement","484":"Change to Company Details","484A":"Form 484 Change to Name, Address or UHC","484B":"Form 484 Change to Officeholder(s) and Special Purpose Co","484C":"Form 484 Change to Shareholder(s) and Shareholding(s)","485":"Statement in relation to Company Solvency","488":"Application to vary Review date","489":"Notification of change of Registered Office of a Registered Body","490":"Notification of change of Officeholders of a Registered Body","491":"Change to Scheme details","492":"Notification of corrections",
  "504":"Notification of appointment as controller","505":"Notification of appointment or cessation as external administrator","506":"Notification of change of address of an external administrator","507":"Report as to affairs","508":"Presentation of accounts by scheme administrator/controller","509":"Presentation of summary of affairs","509D":"Notice of special resolution to wind up company","519":"Notification of court action relating to winding up","520":"Declaration of solvency","522":"Notification of meeting of creditors","523":"Notification of final meeting convened by liquidator","524":"Presentation of accounts and statement by liquidator","529":"Notice of meeting",
  "5100":"Application for registration of a managed investment scheme","5107":"Notification of change of responsible entity","5138":"Notification of commencement or completion of winding up of registered scheme",
  "603":"Initial substantial shareholder notice","604":"Notice of change in interests of substantial shareholder","605":"Notice of ceasing to be a substantial shareholder",
  "6010":"Application for voluntary deregistration of a company","6010A":"Application for voluntary deregistration of a managed investment scheme",
  "7160":"Annual Return of a registered scheme",
  "902":"Notification of information supplementary to a form","903A":"Application for registration as an auditor","903B":"Application for registration as a liquidator","905":"Notification of ceasing to act as or change to details of an auditor","905A":"Notification of ceasing to act as or change to details of a liquidator","907":"Presentation of triennial statement by an auditor","908":"Presentation of statement by a liquidator","912":"Certification of copy of report of affairs","912A":"Annual statement by an auditor","912B":"Annual statement by an authorised audit company",
  "9205":"Pre 1991 Regd Office/Officeholders/Shares","9206":"Pre 1991 Annual Return and Other Documents",
  "RA01":"Notification to register, change details of or cease as an agent","RA03":"Application for ASIC search account","RA05":"Remittance Advice",
  "RC05":"RA Correspondence","RC09":"ASIC company statement",
  "AR":"Annual Return","SOLRES":"Solvency Resolution","POSTINCORP":"Post Incorporation Documents","ADMIN":"Administration Document","APPSHR":"Application for Share(s)","CERTS":"Certificates","CONSENT":"Consent to Act","NOTBENOWN":"Notice of Beneficial Owner","NOTICE":"Notice of Meeting","RESSHR":"Resolution of Shareholder(s)","RESDIR":"Resolution of Director(s)","STRA":"Standard Transfer Form","SDOT":"Declaration of Trust","UNITS":"Units",
  "FS70":"Profit and Loss Statement and Balance Sheet","FS71":"Audit Report","VICBN":"Victorian Registration of Business Name","STATDEC":"Statutory Declaration",
  "Dividend":"Dividend Declaration",
};

const getFormTitle = (formType) => FORM_TITLES[formType] || FORM_INFO[formType]?.label || formType;

const FORM_INFO = {
  "205A": { label: "Notification of Resolution - Change of Company Name", accent: "#0891b2", bg: "#ecfeff" },
  "211":  { label: "Return of particulars of a charge", accent: "#475569", bg: "#f8fafc" },
  "362":  { label: "Notification of appointment as registered agent", accent: "#1d4ed8", bg: "#eff6ff" },
  "480":  { label: "Annual statement", accent: "#c2410c", bg: "#fff7ed" },
  "484":  { label: "Change to company details", accent: "#0f766e", bg: "#f0fdfa" },
  "492":  { label: "Request for correction by ASIC", accent: "#a16207", bg: "#fefce8" },
  "2205": { label: "Notification of resolution regarding shares", accent: "#be185d", bg: "#fff1f2" },
  "Dividend": { label: "Dividend declaration", accent: "#059669", bg: "#ecfdf5" },
  "AR":       { label: "Annual return", accent: "#9333ea", bg: "#faf5ff" },
  "SOLRES":   { label: "Solvency resolution", accent: "#0369a1", bg: "#f0f9ff" },
  "RC09": { label: "ASIC company statement", accent: "#7c3aed", bg: "#f5f3ff" },
};

const CHANGE_ICONS = {
  "Officer address change": "📍", "Officer name change": "✏️",
  "Shareholder address change": "📍", "Shareholder name change": "✏️",
  "Share issue": "📈", "Share cancellation": "📉", "Share structure change": "🔄",
  "Share register change": "📋", "Share division": "➗", "Share conversion": "🔀",
  "Registered office change": "🏢", "Principal business address change": "🏠",
  "Ultimate holding company change": "🏛️", "Concessional fees change": "💰",
};

const SECTION_LABELS = {
  Officers:                { title: "Officer Changes", icon: "👤" },
  ShareIssues:             { title: "Share Issue Summary", icon: "📈" },
  ShareAllocations:        { title: "Share Allocation(s)", icon: "👥" },
  ShareCancellations:      { title: "Share Cancellation(s)", icon: "📉" },
  ShareTransfers:          { title: "Share Transfer(s)", icon: "🔄" },
  ShareBalance:            { title: "Share Balance", icon: "⚖️" },
  ShareConversion:         { title: "Share Conversion", icon: "🔀" },
  ShareConversionDetails:  { title: "Conversion Detail (per holder)", icon: "📋" },
  ShareRedemption:         { title: "Share Redemption", icon: "💰" },
  ShareBuyBack:            { title: "Share Buy Back", icon: "🔙" },
  ShareForfeited:          { title: "Shares Forfeited", icon: "🚫" },
  ShareSubscriber:         { title: "Subscriber Shares", icon: "📝" },
  ShareCancellation:       { title: "Share Cancellation (N)", icon: "✂️" },
  ShareCapitalReturn:      { title: "Capital Return", icon: "💸" },
  ShareChange:             { title: "Share Change", icon: "🔧" },
  ShareNotifyTop20:        { title: "Notify Top 20", icon: "🔔" },
  ShareDivision:           { title: "Share Division/Conversion", icon: "➗" },
  ShareDivisionDetails:    { title: "Division Detail (per holder)", icon: "📋" },
  ShareCapital:            { title: "Share Capital", icon: "🏦" },
  AddressChanges:          { title: "Address Changes", icon: "📍" },
  RegisteredAddresses:     { title: "Registered Addresses", icon: "🏢" },
  Resolution205A:          { title: "Resolution", icon: "📜" },
  Correction492:           { title: "Correction Request", icon: "⚠️" },
  DividendDetail:          { title: "Dividend Details", icon: "💵" },
};

const SHARE_MOVEMENT_KEYS = [
  "ShareBalance", "ShareConversion", "ShareRedemption", "ShareBuyBack",
  "ShareForfeited", "ShareSubscriber", "ShareCancellation",
  "ShareCapitalReturn", "ShareChange", "ShareNotifyTop20",
];

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

/**
 * Normalizes incoming JSON data.
 * Accepts either a plain array or an API envelope { IsSuccess, DataObject: [...] }.
 * Fields are the SP column names: CO_CODE, CompanyName, ACN, DOC_NO, DOC_TRACK_NO,
 * FORM_TYPE, FORM_DESC, STATUS, PRINT_DATE, SIGN_DATE, LODGE_DATE, FORM_DATE,
 * CREATED_ON, TimelineDate, CREATED_BY, ProcessedBy, MEMO, ASIC_DOC_NO,
 * SIGN_CODE, SignerName, ChangeSummary.
 */
function normalizeData(rawData) {
  // Unwrap API envelope if present: { IsSuccess, DataObject: [...] }
  if (rawData && !Array.isArray(rawData) && Array.isArray(rawData.DataObject)) {
    rawData = rawData.DataObject;
  }
  if (!rawData || !rawData.length) return [];
  return rawData;
}

const getForm = (t) => FORM_INFO[t] || { label: "Document", accent: "#64748b", bg: "#f8fafc" };
const fmtDate = (d) => !d ? "\u2014" : new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
const fmtDateTime = (d) => {
  if (!d) return "\u2014";
  const x = new Date(d);
  return x.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) + " " + x.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
};
const getYear = (d) => new Date(d).getFullYear();

function parseChangeSummary(summary) {
  if (!summary) return { flags: [], sections: [] };
  const parts = summary.split(" || ");
  let flags = [];
  const sections = [];
  parts.forEach((part) => {
    const t = part.trim();
    const ci = t.indexOf(": ");
    if (ci === -1) { flags = t.split(" | ").map((s) => s.trim()).filter(Boolean); return; }
    const key = t.substring(0, ci);
    const value = t.substring(ci + 2);
    if (SECTION_LABELS[key]) {
      const detailed = value.includes(" ~ ");
      sections.push({ key, items: detailed ? value.split(" ;; ").filter(Boolean) : value.split(", ").filter(Boolean), isDetailed: detailed });
    } else if (!flags.length) {
      flags = t.split(" | ").map((s) => s.trim()).filter(Boolean);
    }
  });
  return { flags, sections };
}

function parseDetailField(fi) {
  const cp = fi.indexOf(": ");
  if (cp > 0 && cp < 25) return { label: fi.substring(0, cp), value: fi.substring(cp + 2) };
  return { label: null, value: fi };
}

/* ═══════════════════════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════════════════════ */
function OfficerLine({ text }) {
  const dm = text.match(/\((\d{2}\/\d{2}\/\d{4})\)/);
  const c = text.replace(/\s*\(\d{2}\/\d{2}\/\d{4}\)/, "");
  const mod = c.toLowerCase().includes("ceased") ? "ceased" : "appointed";
  return (
    <div className="dt-officer">
      <span className="dt-officer__info">
        <span className={`dt-officer__dot dt-officer__dot--${mod}`} />
        <span className={`dt-officer__text dt-officer__text--${mod}`}>{c}</span>
      </span>
      {dm && <span className="dt-officer__date">{dm[1]}</span>}
    </div>
  );
}

function DetailLine({ text }) {
  const dm = text.match(/\((\d{2}\/\d{2}\/\d{4})\)/);
  const c = text.replace(/\s*\(\d{2}\/\d{2}\/\d{4}\)/, "");
  return (
    <div className="dt-detail-line">
      <span className="dt-detail-line__text">{c}</span>
      {dm && <span className="dt-detail-line__date">{dm[1]}</span>}
    </div>
  );
}

function SubcardFields({ fields, startIdx = 0, dateLabel = "Date" }) {
  return fields.slice(startIdx).map((fi, i) => {
    const { label, value } = typeof fi === "string" ? parseDetailField(fi) : fi;
    const isDate = label === dateLabel || label === "Date of Issue" || label === "Date of Change" || label === "Date of Resolution";
    const isClass = label === "Class";
    const isB = value === "Yes" || value === "No";
    let cls = "";
    if (isDate) cls = "dt-subcard__detail-value--teal";
    else if (isClass) cls = "dt-subcard__detail-value--purple";
    else if (isB) cls = value === "Yes" ? "dt-subcard__detail-value--green" : "dt-subcard__detail-value--red";
    return (
      <div key={i} className="dt-subcard__detail-row">
        {label ? (<><span className="dt-subcard__detail-label">{label}:</span><span className={cls}>{value}</span></>) : <span>{value}</span>}
      </div>
    );
  });
}

function ResolutionCard({ text }) {
  const fields = text.split(" ~ ").map((s) => s.trim());
  return (
    <div className="dt-subcard dt-subcard--cyan">
      {fields.map((fi, i) => {
        const { label, value } = parseDetailField(fi);
        const isDate = label === "Date of Resolution";
        const isRes = label === "Resolution";
        return (
          <div key={i} style={{ padding: isRes ? "4px 0 0" : "1px 0", fontSize: isRes ? 12 : 11 }}>
            {label ? (<><div className="dt-subcard__detail-label" style={{ fontSize: 10, marginBottom: 1 }}>{label}</div><div className={isDate ? "dt-subcard__detail-value--teal" : ""} style={{ color: isDate ? undefined : "#0e7490", lineHeight: 1.4 }}>{value}</div></>) : <span>{value}</span>}
          </div>
        );
      })}
    </div>
  );
}

function ShareDivisionSummaryCard({ text }) {
  const fields = text.split(" ~ ").map((s) => s.trim());
  if (fields.length < 2) return <div className="dt-subcard__detail-row">{text}</div>;
  return (
    <div className="dt-subcard dt-subcard--purple">
      <div className="dt-subcard__title-text" style={{ color: "#7c3aed", marginBottom: 3 }}>➡️ {fields[0]}</div>
      <SubcardFields fields={fields} startIdx={1} />
    </div>
  );
}

function DivisionDetailCard({ text }) {
  const f = text.split(" ~ ").map((s) => s.trim());
  if (f.length < 3) return <div className="dt-subcard__detail-row">{text}</div>;
  const isOld = f[0] === "OLD";
  const mod = isOld ? "out" : "in";
  const icon = isOld ? "➖" : "➕";
  return (
    <div className="dt-subcard dt-subcard--neutral">
      <div className="dt-subcard__header">
        <span className={`dt-dir-badge dt-dir-badge--${mod}`}>{icon} {f[0]}</span>
        <span className="dt-subcard__name">{f[1].replace(/\s+/g, " ")}</span>
      </div>
      <SubcardFields fields={f} startIdx={2} />
    </div>
  );
}

function ShareIssueSummaryCard({ text }) {
  const fields = text.split(" ~ ").map((s) => s.trim());
  if (fields.length < 2) return <div className="dt-subcard__detail-row">{text}</div>;
  return (
    <div className="dt-subcard dt-subcard--green">
      <div className="dt-subcard__title-text" style={{ color: "#15803d", marginBottom: 2 }}>📄 {fields[0]}</div>
      <SubcardFields fields={fields} startIdx={1} />
    </div>
  );
}

function PersonShareCard({ text }) {
  const fields = text.split(" ~ ").map((s) => s.trim());
  if (fields.length < 2) return <div className="dt-subcard__detail-row">{text}</div>;
  return (
    <div className="dt-subcard dt-subcard--neutral">
      <div className="dt-subcard__name" style={{ marginBottom: 2 }}>{fields[0].replace(/\s+/g, " ")}</div>
      <SubcardFields fields={fields} startIdx={1} />
    </div>
  );
}

function AddressCard({ text }) {
  const f = text.split(" ~ ").map((s) => s.trim());
  if (f.length < 2) return <div className="dt-subcard__detail-row">{text}</div>;
  const name = f[0].replace(/\s+/g, " ");
  const role = f[1] || "";
  const addr = f.slice(2).join(" ").trim();
  const roleMod = role.includes("&") ? "both" : role === "Officer" ? "officer" : "shareholder";
  return (
    <div className="dt-subcard dt-subcard--neutral">
      <div className="dt-subcard__header">
        <span className="dt-subcard__name">{name}</span>
        {role && <span className={`dt-role-badge dt-role-badge--${roleMod}`}>{role}</span>}
      </div>
      {addr && <div style={{ fontSize: 11, color: "#475569" }}>📍 {addr}</div>}
    </div>
  );
}

function RegisteredAddressCard({ text }) {
  const f = text.split(" ~ ").map((s) => s.trim());
  const label = f[0] || ""; const eff = f[1] || ""; const addr = f.slice(2).join(" ").trim();
  const icon = label.includes("Registered") ? "🏢" : "🏠";
  return (
    <div className="dt-subcard dt-subcard--neutral">
      <div className="dt-subcard__name" style={{ marginBottom: 2 }}>{icon} {label}</div>
      {eff && <div className="dt-regaddr__effective"><span className="dt-subcard__detail-label" style={{ minWidth: 55 }}>Effective:</span><span className="dt-subcard__detail-value--teal">{eff.replace("Effective: ", "")}</span></div>}
      {addr && <div className="dt-regaddr__address"><span className="dt-subcard__detail-label" style={{ minWidth: 55 }}>Address:</span><span>{addr}</span></div>}
    </div>
  );
}

function TransferCard({ text }) {
  const f = text.split(" ~ ").map((s) => s.trim());
  if (f.length < 3) return <div className="dt-subcard__detail-row">{text}</div>;
  const direction = f[0];
  const isOut = direction === "FROM" || direction === "OUT";
  const mod = isOut ? "out" : "in";
  const icon = isOut ? "⬆" : "⬇";
  return (
    <div className="dt-subcard dt-subcard--neutral">
      <div className="dt-subcard__header">
        <span className={`dt-dir-badge dt-dir-badge--${mod}`}>{icon} {direction}</span>
        <span className="dt-subcard__name">{f[1].replace(/\s+/g, " ")}</span>
      </div>
      <SubcardFields fields={f} startIdx={2} />
    </div>
  );
}

function CorrectionCard({ text }) {
  const f = text.split(" ~ ").map((s) => s.trim());
  const fr = f[0] || ""; const ad = f[1] || ""; const det = f.slice(2).join(" ").replace(/\s+/g, " ").trim();
  return (
    <div className="dt-subcard dt-subcard--amber">
      <div className="dt-correction__title">⚠️ {fr}</div>
      {ad && <div className="dt-correction__asic"><span className="dt-subcard__detail-label" style={{ minWidth: 55 }}>ASIC Doc:</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{ad.replace("ASIC Doc: ", "")}</span></div>}
      {det && <div className="dt-correction__detail">{det}</div>}
    </div>
  );
}

function DividendDetailCard({ text }) {
  const f = text.split(" ~ ").map(s => s.trim());
  const desc = f[0] || "";
  const fields = {};
  f.slice(1).forEach(fi => {
    const cp = fi.indexOf(": ");
    if (cp > 0) fields[fi.substring(0, cp)] = fi.substring(cp + 2);
  });
  const type = fields["Type"] || "—";
  const isInterim = type.includes("Interim");
  const isFinal = type.includes("Final");
  return (
    <div className="dt-subcard dt-subcard--green">
      {desc && <div className="dt-subcard__header"><span className="dt-subcard__title">💵 {desc}</span></div>}
      <div className="dt-detail-grid">
        {fields["Class"] && <div className="dt-detail-row"><span className="dt-detail-label">Share Class</span><span className="dt-detail-value">{fields["Class"]}</span></div>}
        <div className="dt-detail-row"><span className="dt-detail-label">Type</span><span className="dt-detail-value">{isInterim && <Badge bg="info" className="me-1" style={{fontSize:9}}>Interim</Badge>}{isFinal && <Badge bg="success" style={{fontSize:9}}>Final</Badge>}{!isInterim && !isFinal && "—"}</span></div>
        {fields["Declaration Date"] && <div className="dt-detail-row"><span className="dt-detail-label">Declaration Date</span><span className="dt-detail-value dt-detail-value--mono">{fields["Declaration Date"]}</span></div>}
        {fields["Books Close Date"] && <div className="dt-detail-row"><span className="dt-detail-label">Books Close Date</span><span className="dt-detail-value dt-detail-value--mono">{fields["Books Close Date"]}</span></div>}
        {fields["Payable Date"] && <div className="dt-detail-row"><span className="dt-detail-label">Payable Date</span><span className="dt-detail-value dt-detail-value--mono">{fields["Payable Date"]}</span></div>}
        {fields["Financial Year End"] && <div className="dt-detail-row"><span className="dt-detail-label">Financial Year End</span><span className="dt-detail-value dt-detail-value--mono">{fields["Financial Year End"]}</span></div>}
      </div>
      <div className="dt-subcard__divider" />
      <div className="dt-detail-grid">
        {fields["Dividend Per Share"] && <div className="dt-detail-row"><span className="dt-detail-label">Dividend Per Share</span><span className="dt-detail-value dt-detail-value--accent">{fields["Dividend Per Share"]}</span></div>}
        {fields["Total Dividend"] && <div className="dt-detail-row"><span className="dt-detail-label">Total Dividend</span><span className="dt-detail-value dt-detail-value--accent">{fields["Total Dividend"]}</span></div>}
        {fields["Franked"] && <div className="dt-detail-row"><span className="dt-detail-label">Franked</span><span className="dt-detail-value">{fields["Franked"]}</span></div>}
        {fields["Unfranked"] && <div className="dt-detail-row"><span className="dt-detail-label">Unfranked</span><span className="dt-detail-value">{fields["Unfranked"]}</span></div>}
        {fields["Company Tax Rate"] && <div className="dt-detail-row"><span className="dt-detail-label">Company Tax Rate</span><span className="dt-detail-value">{fields["Company Tax Rate"]}</span></div>}
        {fields["Franked Amount"] && <div className="dt-detail-row"><span className="dt-detail-label">Franked Amount</span><span className="dt-detail-value dt-detail-value--mono">{fields["Franked Amount"]}</span></div>}
        {fields["Unfranked Amount"] && <div className="dt-detail-row"><span className="dt-detail-label">Unfranked Amount</span><span className="dt-detail-value dt-detail-value--mono">{fields["Unfranked Amount"]}</span></div>}
      </div>
      {(fields["Shareholder Resolutions"] || fields["Short Meeting Notice"] || fields["Tax Withheld"]) && <>
        <div className="dt-subcard__divider" />
        <div className="dt-detail-grid">
          {fields["Shareholder Resolutions"] && <div className="dt-detail-row"><span className="dt-detail-label">Shareholder Resolutions</span><span className="dt-detail-value">✓ Included</span></div>}
          {fields["Short Meeting Notice"] && <div className="dt-detail-row"><span className="dt-detail-label">Short Meeting Notice</span><span className="dt-detail-value">✓ Yes</span></div>}
          {fields["Tax Withheld"] && <div className="dt-detail-row"><span className="dt-detail-label">Tax Withheld</span><span className="dt-detail-value">✓ Yes</span></div>}
        </div>
      </>}
    </div>
  );
}

function SectionBlock({ sectionKey, items, isDetailed }) {
  const info = SECTION_LABELS[sectionKey] || { title: sectionKey, icon: "•" };
  const Renderer = {
    Correction492: CorrectionCard, AddressChanges: AddressCard, RegisteredAddresses: RegisteredAddressCard,
    ShareIssues: ShareIssueSummaryCard, ShareDivision: ShareDivisionSummaryCard, Resolution205A: ResolutionCard,
    DividendDetail: DividendDetailCard,
  }[sectionKey];
  const isTransfer = sectionKey === "ShareTransfers" || SHARE_MOVEMENT_KEYS.includes(sectionKey);
  const isDivDetail = sectionKey === "ShareDivisionDetails" || sectionKey === "ShareConversionDetails";
  const isAlloc = sectionKey === "ShareAllocations";
  const isOfficer = sectionKey === "Officers";

  const renderItems = () => {
    if (Renderer) return <div className="dt-section__items">{items.map((it, i) => <Renderer key={i} text={it} />)}</div>;
    if (isTransfer) return <div className="dt-section__items">{items.map((it, i) => <TransferCard key={i} text={it} />)}</div>;
    if (isDivDetail) return <div className="dt-section__items">{items.map((it, i) => <DivisionDetailCard key={i} text={it} />)}</div>;
    if (isAlloc || isDetailed) return <div className="dt-section__items">{items.map((it, i) => <PersonShareCard key={i} text={it} />)}</div>;
    if (isOfficer) return items.map((it, i) => <OfficerLine key={i} text={it} />);
    return items.map((it, i) => <DetailLine key={i} text={it} />);
  };
  return (
    <div className="dt-section">
      <div className="dt-section__title">{info.icon} {info.title}</div>
      <div className="dt-section__body">{renderItems()}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Detail Popup (Bootstrap Modal)
   ═══════════════════════════════════════════════════════════════ */
function DetailPopup({ item, show, onClose, documentUrl }) {
  const f = getForm(item.FORM_TYPE);
  const { flags, sections } = parseChangeSummary(item.ChangeSummary);
  const hc2 = flags.length > 0 || sections.length > 0;
  const ft = item.FORM_TYPE;
  const showChanges = ["484", "492", "2205", "205A", "211", "Dividend"].includes(ft) && hc2;
  const changesMod = ({ "492": "492", "2205": "2205", "205A": "205A", "211": "211", "Dividend": "Dividend" })[ft] || "484";
  const changesTitle = ({ "492": "Correction Details", "2205": "Share Resolution Details", "205A": "Resolution Details", "211": "Conversion Details", "Dividend": "Dividend Details" })[ft] || "Changes in this 484";
  const changesColor = ({ "492": "#a16207", "2205": "#be185d", "205A": "#0891b2", "211": "#475569", "Dividend": "#059669" })[ft] || "#0f766e";
  const isStatusOk = item.STATUS === "VALIDATED" || item.STATUS === "LODGED";

  // Build the view document URL by replacing placeholders
  const viewUrl = documentUrl
    ? documentUrl
        .replace("{DOC_NO}", encodeURIComponent(item.DOC_NO || ""))
        .replace("{CO_CODE}", encodeURIComponent(item.CO_CODE || ""))
        .replace("{FORM_TYPE}", encodeURIComponent(item.FORM_TYPE || ""))
        .replace("{DOC_TRACK_NO}", encodeURIComponent(item.DOC_TRACK_NO || ""))
        .replace("{ASIC_DOC_NO}", encodeURIComponent(item.ASIC_DOC_NO || ""))
    : null;

  const rows = [
    { label: "Form Type", value: item.FORM_TYPE },
    { label: "Description", value: item.FORM_DESC || f.label },
    { label: "Prime Doc No", value: item.DOC_NO },
    { label: "ASIC Doc No", value: item.ASIC_DOC_NO || "\u2014" },
    { label: "Status", value: item.STATUS || "\u2014", isStatus: true },
    null,
    { label: "Form Date", value: fmtDate(item.FORM_DATE) },
    { label: "Print Date", value: fmtDate(item.PRINT_DATE) },
    { label: "Sign Date", value: fmtDate(item.SIGN_DATE) },
    { label: (ft === "480" || ft === "RC09") ? "Received" : "Lodge Date", value: fmtDateTime(item.LODGE_DATE) },
    { label: "Created On", value: fmtDateTime(item.CREATED_ON) },
    null,
    { label: "Signer", value: item.SignerName || "\u2014" },
  ];
  if (item.CREATED_BY) rows.push({ label: "Created By", value: item.CREATED_BY });
  if (item.MEMO) rows.push(null, { label: "Notes", value: item.MEMO });

  return (
    <Modal show={show} onHide={onClose} centered className="dt-popup" animation>
      <Modal.Header closeButton>
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          <span className="dt-popup__form-badge" title={getFormTitle(ft)} style={{ color: f.accent, background: f.bg, border: `1px solid ${f.accent}30` }}>{ft}</span>
          <span className="dt-popup__form-label">{item.FORM_DESC || f.label}</span>
        </div>
      </Modal.Header>
      <Modal.Body>
        {showChanges && (
          <div className={`dt-popup__changes dt-popup__changes--${changesMod}`}>
            <div className="dt-popup__changes-title" style={{ color: changesColor }}>{changesTitle}</div>
            {flags.length > 0 && (
              <div className="dt-popup__flags">
                {flags.map((fl, i) => <span key={i} className="dt-popup__flag-badge"><span className="dt-popup__flag-icon">{CHANGE_ICONS[fl] || "•"}</span>{fl}</span>)}
              </div>
            )}
            <div className="dt-popup__sections">{sections.map((sec, i) => <SectionBlock key={i} sectionKey={sec.key} items={sec.items} isDetailed={sec.isDetailed} />)}</div>
          </div>
        )}
        <div className="dt-popup__rows">
          {rows.map((row, i) => {
            if (row === null) return <div key={`s${i}`} className="dt-popup__row-divider" />;
            return (
              <div key={i} className="dt-popup__row">
                <span className="dt-popup__row-label">{row.label}</span>
                {row.isStatus && row.value !== "\u2014"
                  ? <Badge className={`dt-card__status-badge dt-card__status-badge--${isStatusOk ? "ok" : "other"}`}>{row.value}</Badge>
                  : <span className="dt-popup__row-value">{row.value}</span>}
              </div>
            );
          })}
        </div>
        {viewUrl && (
          <div className="dt-popup__view-doc">
            <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="dt-popup__view-doc-link">
              📄 View Document
            </a>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Timeline Card
   ═══════════════════════════════════════════════════════════════ */
function TimelineCard({ item, side, visible, onClick }) {
  const f = getForm(item.FORM_TYPE);
  const { flags, sections } = parseChangeSummary(item.ChangeSummary);
  const hasAny = flags.length > 0 || sections.length > 0;
  const has492 = item.FORM_TYPE === "492" && sections.some((s) => s.key === "Correction492");
  const isL = side === "left";
  const isStatusOk = item.STATUS === "VALIDATED" || item.STATUS === "LODGED";

  const isDividend = item.FORM_TYPE === "Dividend";
  const divDesc = isDividend && item.ChangeSummary
    ? item.ChangeSummary.replace(/^DividendDetail:\s*/, "").split(" ~ ")[0]
    : null;

  return (
    <div className={`dt-card dt-card--${side} ${visible ? `dt-card--visible-${side}` : "dt-card--hidden"}`} onClick={onClick}>
      <div className={`dt-card__connector dt-card__connector--${side}`} style={{ background: `linear-gradient(${isL ? "90deg" : "270deg"}, transparent, ${f.accent}40)` }} />
      <div className={`dt-card__dot dt-card__dot--${side}`} style={{ background: f.accent, boxShadow: `0 0 0 2px ${f.accent}30` }} />
      <div className="dt-card__body" style={{ background: f.bg, borderColor: `${f.accent}22` }}>
        <div className={`dt-card__date dt-card__date--${side}`}>{fmtDate(item.TimelineDate)}</div>
        <div className="dt-card__badges">
          <span className="dt-card__form-badge" title={getFormTitle(item.FORM_TYPE)} style={{ color: f.accent, background: `${f.accent}10`, border: `1px solid ${f.accent}25` }}>{item.FORM_TYPE}</span>
          {item.STATUS && <Badge className={`dt-card__status-badge dt-card__status-badge--${isStatusOk ? "ok" : "other"}`}>{item.STATUS}</Badge>}
        </div>
        <div className="dt-card__desc">{divDesc || item.FORM_DESC || f.label}</div>
        <div className="dt-card__doc-ref">Doc: {item.DOC_NO}{item.ASIC_DOC_NO ? ` · ASIC: ${item.ASIC_DOC_NO}` : ""}</div>
        {hasAny && (
          <div className="dt-card__flags">
            {flags.map((flag, i) => <span key={`f${i}`} className="dt-card__flag">{CHANGE_ICONS[flag] || "•"} {flag}</span>)}
            {!flags.length && !has492 && sections.map((sec, i) => { const info = SECTION_LABELS[sec.key]; return info ? <span key={`s${i}`} className="dt-card__flag">{info.icon} {info.title}</span> : null; })}
            {has492 && <span className="dt-card__flag dt-card__flag--correction">⚠️ Correction</span>}
          </div>
        )}
        <div className="dt-card__meta">
          {item.LODGE_DATE && <span className="dt-card__meta-item">{(item.FORM_TYPE === "480" || item.FORM_TYPE === "RC09") ? "Recv" : "Lodged"}: {fmtDate(item.LODGE_DATE)}</span>}
          {item.SignerName && <span className="dt-card__meta-item dt-card__meta-item--signer">✍ {item.SignerName}</span>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */
export default function DocumentTimeline({ data: rawData = [], loading = false, error = null, onDocumentClick = null, documentUrl = null }) {
  const data = normalizeData(rawData);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [popupItem, setPopupItem] = useState(null);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [collapsedYears, setCollapsedYears] = useState(new Set());
  const [sortOrder, setSortOrder] = useState("desc");
  const containerRef = useRef(null);

  useEffect(() => { data.forEach((_, i) => { setTimeout(() => setVisibleItems((p) => new Set([...p, i])), 120 * i); }); }, [data]);

  const handleDownloadSVG = () => {
    const el = containerRef.current;
    if (!el) return;
    const clone = el.cloneNode(true);
    const computed = (node) => {
      if (node.nodeType !== 1) return;
      const s = window.getComputedStyle(node);
      const css = Array.from(s).map((k) => `${k}:${s.getPropertyValue(k)}`).join(";");
      node.setAttribute("style", css);
      Array.from(node.children).forEach(computed);
    };
    computed(clone);
    const w = el.scrollWidth, h = el.scrollHeight;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml">${clone.outerHTML}</div></foreignObject></svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `${(data[0]?.CompanyName || "Timeline").replace(/\s+/g, "_")}_Timeline.svg`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="dt-state-wrapper"><div className="dt-loading"><Spinner animation="border" variant="success" className="mb-3" /><div>Loading timeline...</div></div></div>;
  if (error) return <div className="dt-state-wrapper"><Alert variant="danger" className="dt-error"><div className="dt-error__icon">⚠️</div><div className="dt-error__title">Error loading timeline</div><div className="dt-error__msg">{error}</div></Alert></div>;
  if (!data || data.length === 0) return <div className="dt-state-wrapper"><div className="dt-empty"><div className="dt-empty__icon">📄</div><div>No documents found for this company.</div></div></div>;

  const toggleFilter = (ft) => { setActiveFilters((prev) => { const next = new Set(prev); next.has(ft) ? next.delete(ft) : next.add(ft); return next; }); };
  const toggleYear = (yr) => { setCollapsedYears((prev) => { const next = new Set(prev); next.has(yr) ? next.delete(yr) : next.add(yr); return next; }); };
  const sortedData = [...data].sort((a, b) => sortOrder === "desc" ? new Date(b.TimelineDate) - new Date(a.TimelineDate) : new Date(a.TimelineDate) - new Date(b.TimelineDate));
  const filteredData = activeFilters.size === 0 ? sortedData : sortedData.filter((item) => activeFilters.has(item.FORM_TYPE));
  const company = data[0] || {};
  const groupedByYear = filteredData.reduce((a, item) => { const y = getYear(item.TimelineDate); if (!a[y]) a[y] = []; a[y].push(item); return a; }, {});
  const yearEntries = Object.entries(groupedByYear).sort((a, b) => sortOrder === "desc" ? b[0] - a[0] : a[0] - b[0]);
  const formCounts = data.reduce((a, item) => { a[item.FORM_TYPE] = (a[item.FORM_TYPE] || 0) + 1; return a; }, {});
  let globalIdx = 0;

  return (
    <div className="dt-root">
      {popupItem && <DetailPopup item={popupItem} show={!!popupItem} onClose={() => setPopupItem(null)} documentUrl={documentUrl} />}
      <div className="dt-container" ref={containerRef}>
        {/* Header */}
        <div className="dt-header">
          <div className="dt-header__top-bar">
            <div className="dt-header__pill">Document Timeline</div>
            <button className="dt-download-btn" onClick={handleDownloadSVG}>Download SVG</button>
          </div>
          <h1 className="dt-header__title">{company.CompanyName}</h1>
          <div className="dt-header__acn">ACN {company.ACN}</div>
          <div className="dt-header__summary">{filteredData.length}{activeFilters.size > 0 ? ` of ${data.length}` : ""} documents · {fmtDate(data[data.length - 1]?.TimelineDate)} — {fmtDate(data[0]?.TimelineDate)}</div>
          <div className="dt-filters">
            {Object.entries(formCounts).map(([t, c]) => {
              const fi = getForm(t); const isActive = activeFilters.has(t); const anyActive = activeFilters.size > 0; const dimmed = anyActive && !isActive;
              return (
                <div key={t} className={`dt-filter-chip${dimmed ? " dt-filter-chip--dimmed" : ""}`} onClick={() => toggleFilter(t)}
                  title={getFormTitle(t)}
                  style={{ background: dimmed ? undefined : fi.bg, borderColor: isActive ? fi.accent : dimmed ? undefined : `${fi.accent}30`, boxShadow: isActive ? `0 0 0 2px ${fi.accent}20` : "none" }}>
                  <span className="dt-filter-chip__check" style={{ color: dimmed ? undefined : fi.accent }}>{isActive ? "☑" : "☐"}</span>
                  <span className="dt-filter-chip__label" style={{ color: dimmed ? undefined : fi.accent }}>{t}</span>
                  <span className="dt-filter-chip__count">×{c}</span>
                </div>
              );
            })}
            {activeFilters.size > 0 && <div className="dt-filter-clear" onClick={() => setActiveFilters(new Set())}>✕ Clear</div>}
          </div>
          <div className="dt-collapse-controls">
            <button className="dt-collapse-controls__btn" onClick={() => setCollapsedYears(new Set())}>▾ Expand All</button>
            <span className="dt-collapse-controls__sep">|</span>
            <button className="dt-collapse-controls__btn" onClick={() => setCollapsedYears(new Set(yearEntries.map(([y]) => y)))}>▸ Collapse All</button>
            <span className="dt-collapse-controls__sep">|</span>
            <button className="dt-collapse-controls__btn" onClick={() => setSortOrder(s => s === "desc" ? "asc" : "desc")}>{sortOrder === "desc" ? "↓" : "↑"} {sortOrder === "desc" ? "Newest First" : "Oldest First"}</button>
          </div>
        </div>

        {/* Timeline */}
        <div className="dt-spine-wrapper">
          <div className="dt-spine-line" />
          {yearEntries.map(([year, items]) => {
            const isCollapsed = collapsedYears.has(year);
            return (
            <div key={year} className="dt-year-group">
              <div className="dt-year-badge-wrap" onClick={() => toggleYear(year)} style={{ cursor: "pointer" }}>
                <div className={`dt-year-badge${isCollapsed ? " dt-year-badge--collapsed" : ""}`}>
                  <span className={`dt-year-badge__chevron${isCollapsed ? " dt-year-badge__chevron--collapsed" : ""}`}>▾</span>
                  {year}<span className="dt-year-badge__count">{items.length} doc{items.length !== 1 ? "s" : ""}</span>
                </div>
              </div>
              {!isCollapsed && items.map((item, itemIdx) => {
                const idx = globalIdx++;
                const side = idx % 2 === 0 ? "left" : "right";
                const vis = visibleItems.has(data.indexOf(item));
                const curMonth = new Date(item.TimelineDate).getMonth();
                const prevMonth = itemIdx > 0 ? new Date(items[itemIdx - 1].TimelineDate).getMonth() : -1;
                const showMonth = itemIdx === 0 || curMonth !== prevMonth;
                const monthLabel = new Date(item.TimelineDate).toLocaleString("en-AU", { month: "short" }).toUpperCase();
                return (
                  <div key={`${item.DOC_NO}-${item.DOC_TRACK_NO}-${idx}`}>
                    {showMonth && <div className={`dt-month-badge-wrap${itemIdx > 0 ? " dt-month-badge-wrap--spaced" : ""}`}><div className="dt-month-badge">{monthLabel}</div></div>}
                    <div className="dt-card-wrap">
                      <TimelineCard item={item} side={side} visible={vis} onClick={() => { setPopupItem(item); onDocumentClick && onDocumentClick(item); }} />
                    </div>
                  </div>
                );
              })}
              {isCollapsed && <div className="dt-collapsed-hint">{items.length} document{items.length !== 1 ? "s" : ""} hidden</div>}
            </div>
          );})}
          <div className="dt-end-wrap"><div className="dt-end-label">End of timeline</div></div>
        </div>

        {/* Legend */}
        <div className="dt-legend">
          <div className="dt-legend__title">Form Types · Click any card for details</div>
          <div className="dt-legend__list">
            {Object.entries(FORM_INFO).map(([t, info]) => (
              <div key={t} className="dt-legend__item">
                <div className="dt-legend__dot" style={{ background: info.accent }} />
                <span className="dt-legend__code">{t}</span>
                <span className="dt-legend__desc">— {info.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
